import { Col, Row } from 'antd';
import casinoApi from 'api/casinoApi';
import ConditionButton from 'components/Commons/ConditionButton';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useUserDetail from 'hooks/useUserDetail';
import { AwcGame } from 'models/casino';
import { useEffect, useState } from 'react';
import { IframeGame } from './HomeCasinoItem';
import { useAppTranslation } from 'app/hooks';

export interface HomeCasinoProps {}

const HomeCasino: React.FC<HomeCasinoProps> = ({}) => {
  const [games, setGames] = useState<AwcGame[]>([]);
  const [game, setGame] = useState<AwcGame>();
  const { isUserLoggedIn, currentUser } = useUserDetail();
  const handleResponseError = useHandleResponseError();
  const t = useAppTranslation();

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const getFrame = async (game: AwcGame) => {
    const { ok, body } = await casinoApi.playGame({
      gameCode: game.gameCode,
      mobileLogin: false,
      provider: game.provider,
    });

    if (ok && body) {
      return body.url;
    }
    return undefined;
  };

  const retry = async (game: AwcGame, attempt = 1, maxRetries = 3) => {
    const result = await getFrame(game);
    if (!result && attempt < maxRetries) {
      await delay(2000);
      return await retry(game, attempt + 1, maxRetries);
    }
    return result;
  };

  const getGames = async () => {
    const { ok, body } = await casinoApi.getGame({
      size: 10,
      page: 0,
      sort: 'displayOrder,asc',
      'isShowHome.equals': true,
      'enable.equals': true,
      'isMiniGame.equals': false,
    });

    if (ok && body) {
      setGames(body);
      if (isUserLoggedIn && currentUser?.kyc?.status === 'APPROVED') {
        for (const g of body) {
          const urlFrame = (await retry(g)) || '';
          setGames((prevGames) =>
            prevGames.map((game) => (game.gameCode === g.gameCode ? { ...game, urlFrame } : game)),
          );
          await delay(3000);
        }
      }
    }
  };

  const handleRetry = async (game: AwcGame) => {
    setGames((prevGames) =>
      prevGames.map((g) => (g.gameCode === game.gameCode ? { ...g, urlFrame: '' } : g)),
    );
    const urlFrame = (await getFrame(game)) || '';
    setGames((prevGames) =>
      prevGames.map((g) => (g.gameCode === game.gameCode ? { ...g, urlFrame } : g)),
    );
  };

  useEffect(() => {
    getGames();
  }, [isUserLoggedIn, currentUser?.kyc?.status]);

  const handleClickGameInMobile = async (game: AwcGame) => {
    const { ok, body, error } = await casinoApi.playGame({
      gameCode: game.gameCode,
      mobileLogin: window.innerWidth < 1025,
      provider: game.provider,
    });

    if (ok && body) {
      window.open(body.url, '_blank');
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const handleSelectedGame = async (game: AwcGame) => {
    if (isUserLoggedIn) {
      const urlFrame = (await retry(game)) || '';
      setGame({ ...game, urlFrame: urlFrame });
    }
  };
  const handleCloseGame = () => {
    setGame(undefined);
  };

  return (
    <div className="container ">
      <div className="title">{t('header.game')}</div>
      <div className="casino-game-home-list">
        {games && !game?.urlFrame ? (
          <Row className="relative" gutter={[2, 2]} wrap={true}>
            {games
              .slice(0, 10)
              .sort((a, b) => a.order - b.order)
              .map((game) => (
                <Col key={game.gameCode} lg={{ span: 12 }} className="casino-minigame-item">
                  <ConditionButton
                    className="play-button"
                    onClick={() => handleSelectedGame(game)}
                    content={
                      <div className="casino-minigame-image">
                        <img
                          onError={(e) => {
                            e.currentTarget.src = '/no_image.png';
                          }}
                          src={game.image}
                          alt="preview"
                        />
                      </div>
                    }
                    testerCheck={true}
                  />
                </Col>
              ))}
          </Row>
        ) : (
          <div className="casino-iframe">
            {game && (
              <IframeGame
                title={game.name}
                gameUrl={game.urlFrame}
                gameCode={game.gameCode}
                provider={game.provider}
                handleClose={handleCloseGame}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeCasino;
